import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { Goal } from '../types'

interface UseGoalResult {
  goal: Goal | null
  count: number
  loading: boolean
  error: string | null
  setGoal: (targetCount: number) => Promise<void>
}

export function useGoal(userId: string | undefined, year: number): UseGoalResult {
  const [goal, setGoalState] = useState<Goal | null>(null)
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!userId) {
      setLoading(false)
      return
    }

    async function fetchData() {
      setLoading(true)
      setError(null)

      const [goalResult, countResult] = await Promise.all([
        supabase
          .from('goals')
          .select('*')
          .eq('user_id', userId)
          .eq('year', year)
          .maybeSingle(),
        supabase
          .from('log_entries')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId)
          .gte('date_watched', `${year}-01-01`)
          .lte('date_watched', `${year}-12-31`),
      ])

      if (goalResult.error) {
        setError(goalResult.error.message)
      } else {
        setGoalState(goalResult.data as Goal | null)
      }

      if (countResult.error) {
        setError(countResult.error.message)
      } else {
        setCount(countResult.count ?? 0)
      }

      setLoading(false)
    }

    fetchData()
  }, [userId, year])

  const setGoal = useCallback(
    async (targetCount: number) => {
      if (!userId) return

      const { data, error: supabaseError } = await supabase
        .from('goals')
        .upsert(
          { user_id: userId, year, target_count: targetCount },
          { onConflict: 'user_id,year' }
        )
        .select('*')
        .single()

      if (supabaseError) {
        setError(supabaseError.message)
      } else {
        setGoalState(data as Goal)
      }
    },
    [userId, year]
  )

  return { goal, count, loading, error, setGoal }
}
